/** SessionHistoryTab — History tab content for SessionDetailsModal. Displays session activity timeline with event icons, timestamps, and details. */
import React from 'react';
import {
  History,
  Loader2,
  LogIn,
  LogOut,
  Pause,
  Play,
  Factory,
  Layers,
  RotateCcw,
  UserPlus,
  UserMinus,
  Plus,
  Check,
  XCircle,
  AlertTriangle,
  Truck,
  Shield,
  CircleDot,
  User,
  Box,
} from 'lucide-react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { formatCancelReason } from '../../../../utils/cancelReasons';

interface SessionHistoryTabProps {
  session: any;
  isLoadingHistory: boolean;
  historyData: any;
}

const SessionHistoryTab: React.FC<SessionHistoryTabProps> = ({
  session,
  isLoadingHistory,
  historyData,
}) => {
  const { t } = useLanguage();

  // Turn a snake_case value/key into Title Case (e.g. cancelled_status → Cancelled Status)
  const humanize = (value: string): string =>
    value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Translate a closed-vocabulary value via its i18n key, falling back to a
  // title-cased version of the raw value when the key is missing.
  const tVocab = (key: string, raw: string): string => {
    const translated = t(key);
    return translated === key ? humanize(raw) : translated;
  };

  const tReason = (namespace: string, reason: string): string =>
    tVocab(`sessionDetails.${namespace}.${reason}`, reason);

  // Sub-session status label (pending/submitted/received/ready/active/…)
  const tStatus = (status: string): string => tVocab(`status.${status}`, status);

  return (
    <div className="space-y-6">
      {isLoadingHistory ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Session Activity Timeline */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('sessionDetails.activityLog')}
              </h3>
            </div>
            {historyData?.history && historyData.history.length > 0 ? (
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-gray-300 dark:bg-gray-600" />

                <div className="space-y-1">
                  {(() => {
                    // Preprocess: merge unassign+assign pairs (same role, within 2s) into a single "reassigned" event
                    type HistoryEvent = { id: number; action: string; old_status: string | null; new_status: string | null; changed_by: string | null; notes: string | null; created_at: string; worker_name: string | null; employee_number: string | null; role: string | null; station_name: string | null; boxes_at_event: number | null; weight_at_event: number | null; boxes_packed_during_shift: number | null; weight_packed_during_shift: number | null; current_shift_boxes?: number | null; current_shift_weight?: number | null; shift_ended?: boolean | null; _reassigned_from?: string; sub_session_number?: string | null };
                    const raw: HistoryEvent[] = historyData.history;
                    const skipIds = new Set<number>();

                    // First pass: find all assign+unassign pairs to merge
                    const mergeMap = new Map<number, string>(); // assignId -> unassigned worker name
                    for (let i = 0; i < raw.length; i++) {
                      const curr = raw[i];
                      if (!(curr.action === 'operator_assigned' || curr.action === 'packer_assigned') || !curr.worker_name) continue;
                      const rolePrefix = curr.action === 'operator_assigned' ? 'operator' : 'packer';
                      const unassignAction = `${rolePrefix}_unassigned`;
                      // Search nearby events (within 5 positions either direction)
                      for (let j = Math.max(0, i - 5); j < Math.min(raw.length, i + 5); j++) {
                        if (j === i) continue;
                        const other = raw[j];
                        if (other.action === unassignAction && other.role === curr.role &&
                            Math.abs(new Date(curr.created_at).getTime() - new Date(other.created_at).getTime()) < 2000 &&
                            !skipIds.has(other.id)) {
                          skipIds.add(other.id);
                          mergeMap.set(curr.id, other.worker_name || '');
                          break;
                        }
                      }
                    }

                    // Second pass: build merged array
                    const merged: HistoryEvent[] = [];
                    for (const evt of raw) {
                      if (skipIds.has(evt.id)) continue;
                      if (mergeMap.has(evt.id)) {
                        merged.push({ ...evt, _reassigned_from: mergeMap.get(evt.id) || undefined });
                      } else {
                        merged.push(evt);
                      }
                    }
                    return merged;
                  })().map((event: { id: number; action: string; old_status: string | null; new_status: string | null; changed_by: string | null; notes: string | null; created_at: string; worker_name: string | null; employee_number: string | null; role: string | null; station_name: string | null; boxes_at_event: number | null; weight_at_event: number | null; boxes_packed_during_shift: number | null; weight_packed_during_shift: number | null; current_shift_boxes?: number | null; current_shift_weight?: number | null; shift_ended?: boolean | null; _reassigned_from?: string; sub_session_number?: string | null }, index: number) => {
                    const isScanIn = event.action === 'scan_in';
                    const isScanOut = event.action === 'scan_out';
                    const isBreakStart = event.action === 'break_start';
                    const isBreakEnd = event.action === 'break_end';
                    const isBreakEvent = isBreakStart || isBreakEnd;
                    const isScanEvent = isScanIn || isScanOut;
                    const isOperator = event.role === 'operator';
                    const isPacker = event.role === 'packer';
                    const isActiveShift = isScanIn && isPacker && event.shift_ended === false;
                    const isOverrideEvent = event.action?.startsWith('override_');
                    const isOperatorAssigned = event.action === 'operator_assigned';
                    const isPackerAssigned = event.action === 'packer_assigned';
                    const isOperatorUnassigned = event.action === 'operator_unassigned';
                    const isPackerUnassigned = event.action === 'packer_unassigned';
                    const isStationAssigned = event.action === 'station_assigned';
                    const isStationUnassigned = event.action === 'station_unassigned';
                    const isWeightModeChanged = event.action === 'weight_mode_changed';
                    const isAssignmentEvent = isOperatorAssigned || isPackerAssigned || isOperatorUnassigned || isPackerUnassigned || isStationAssigned || isStationUnassigned;
                    const isReassignment = !!(event._reassigned_from);
                    const isAssigned = isOperatorAssigned || isPackerAssigned;
                    const isUnassigned = isOperatorUnassigned || isPackerUnassigned;

                    // Parse station/operator/packer/weight-mode assignment notes from supervisor save
                    let assignmentNotes: { station_name?: string; old_station_name?: string; operator_name?: string; packer_name?: string; sub_session_number?: string; old_mode?: string; new_mode?: string } | null = null;
                    if ((isStationAssigned || isStationUnassigned || isWeightModeChanged || isOperatorAssigned || isPackerAssigned) && event.notes && !event.worker_name) {
                      try {
                        assignmentNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse override notes JSON if applicable
                    let overrideNotes: { supervisor_name?: string; reason?: string; operator_name?: string; previous_mode?: string; new_mode?: string } | null = null;
                    if (isOverrideEvent && event.notes) {
                      try {
                        overrideNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse box_cancelled notes JSON if applicable
                    let boxCancelledNotes: { box_id?: string; original_box_number?: string; barcode_number?: string; weight?: number; reason?: string; item_number?: string } | null = null;
                    if (event.action === 'box_cancelled' && event.notes) {
                      try {
                        boxCancelledNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse material_issue notes JSON if applicable
                    let materialIssueNotes: { reason?: string; damaged_weight?: number; weight_source?: string; item_number?: string; transfer_number?: string; rej_transfer_number?: string; weight_before?: number; weight_after?: number; target_before?: number; target_after?: number; operator_name?: string; barcode_raw?: string | null } | null = null;
                    if (event.action === 'material_issue' && event.notes) {
                      try {
                        materialIssueNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse weight_modified notes JSON if applicable
                    let weightModifiedNotes: { sub_session_number?: string; old_weight?: number; new_weight?: number; difference?: number; status_at_change?: string } | null = null;
                    if (event.action === 'weight_modified' && event.notes) {
                      try {
                        weightModifiedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse subdivided notes JSON if applicable
                    let subdividedNotes: { original_sub_session_number?: string; new_sub_session_number?: string; split_weight?: number; original_weight?: number; original_remaining_weight?: number; reason?: string; reason_other?: string | null } | null = null;
                    if (event.action === 'subdivided' && event.notes) {
                      try {
                        subdividedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse material_added notes JSON if applicable
                    let materialAddedNotes: { item_number?: string; transfer_number?: string; weight?: number; weight_before?: number; weight_after?: number; reason?: string; reason_other?: string | null; result?: string } | null = null;
                    if (event.action === 'material_added' && event.notes) {
                      try {
                        materialAddedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse status_changed notes JSON if applicable (WCO-2705)
                    let statusChangedNotes: { from?: string; to?: string } | null = null;
                    if (event.action === 'status_changed' && event.notes) {
                      try {
                        statusChangedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse finish_product_added notes JSON if applicable (WCO-2705)
                    let finishProductAddedNotes: { sub_session_number?: string; lot_id?: string; item_number?: string; transfer_number?: string; weight?: number; reason?: string } | null = null;
                    if (event.action === 'finish_product_added' && event.notes) {
                      try {
                        finishProductAddedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Parse transfer_verified notes JSON if applicable
                    let transferVerifiedNotes: { transfer_number?: string; total_weight?: number; cases_received?: number; items?: Array<{ item: string; action: string; weight: number; quantity?: number }> } | null = null;
                    if (event.action === 'transfer_verified' && event.notes) {
                      try {
                        transferVerifiedNotes = JSON.parse(event.notes);
                      } catch {
                        // Not JSON, leave as null
                      }
                    }

                    // Format time only (not full date) for compact display
                    const eventTime = new Date(event.created_at).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    });
                    const eventDate = new Date(event.created_at).toLocaleDateString('en-US', {
                      month: '2-digit',
                      day: '2-digit'
                    });

                    return (
                      <div
                        key={`${event.action}-${event.id}`}
                        className="relative flex items-start gap-4 py-3"
                      >
                        {/* Timeline node with icon */}
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${
                          isScanIn ? 'bg-cyan-100 dark:bg-cyan-900/50' :
                          isScanOut ? 'bg-orange-100 dark:bg-orange-900/50' :
                          isBreakStart ? 'bg-amber-100 dark:bg-amber-900/50' :
                          isBreakEnd ? 'bg-green-100 dark:bg-green-900/50' :
                          isStationAssigned ? 'bg-blue-100 dark:bg-blue-900/50' :
                          isStationUnassigned ? 'bg-red-100 dark:bg-red-900/50' :
                          isWeightModeChanged ? 'bg-purple-100 dark:bg-purple-900/50' :
                          isReassignment ? 'bg-amber-100 dark:bg-amber-900/50' :
                          isOperatorAssigned ? 'bg-green-100 dark:bg-green-900/50' :
                          isPackerAssigned ? 'bg-blue-100 dark:bg-blue-900/50' :
                          isOperatorUnassigned ? 'bg-red-100 dark:bg-red-900/50' :
                          isPackerUnassigned ? 'bg-red-100 dark:bg-red-900/50' :
                          event.action === 'created' ? 'bg-blue-100 dark:bg-blue-900/50' :
                          event.action === 'started' || event.action === 'sub_session_started' ? 'bg-green-100 dark:bg-green-900/50' :
                          event.action === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                          event.action === 'cancelled' ? 'bg-red-100 dark:bg-red-900/50' :
                          event.action === 'box_cancelled' ? 'bg-red-100 dark:bg-red-900/50' :
                          event.action === 'material_issue' ? 'bg-orange-100 dark:bg-orange-900/50' :
                          event.action === 'material_added' ? 'bg-green-100 dark:bg-green-900/50' :
                          event.action === 'finish_product_added' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                          event.action === 'transfer_verified' ? 'bg-cyan-100 dark:bg-cyan-900/50' :
                          event.action === 'paused' || event.action === 'sub_session_paused' ? 'bg-yellow-100 dark:bg-yellow-900/50' :
                          event.action === 'resumed' || event.action === 'sub_session_resumed' ? 'bg-green-100 dark:bg-green-900/50' :
                          isOverrideEvent ? 'bg-purple-100 dark:bg-purple-900/50' :
                          'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          {isScanIn ? (
                            <LogIn className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                          ) : isScanOut ? (
                            <LogOut className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          ) : isBreakStart ? (
                            <Pause className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          ) : isBreakEnd ? (
                            <Play className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : isStationAssigned ? (
                            <Factory className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : isStationUnassigned ? (
                            <Factory className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : isWeightModeChanged ? (
                            <Layers className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          ) : isReassignment ? (
                            <RotateCcw className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          ) : isOperatorAssigned ? (
                            <UserPlus className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : isPackerAssigned ? (
                            <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : isUnassigned ? (
                            <UserMinus className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : event.action === 'created' ? (
                            <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : event.action === 'started' || event.action === 'sub_session_started' ? (
                            <Play className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : event.action === 'completed' ? (
                            <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          ) : event.action === 'cancelled' ? (
                            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : event.action === 'box_cancelled' ? (
                            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : event.action === 'material_issue' ? (
                            <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          ) : event.action === 'material_added' ? (
                            <Truck className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : event.action === 'finish_product_added' ? (
                            <Box className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          ) : event.action === 'transfer_verified' ? (
                            <Check className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                          ) : event.action === 'paused' || event.action === 'sub_session_paused' ? (
                            <Pause className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                          ) : event.action === 'resumed' || event.action === 'sub_session_resumed' ? (
                            <Play className="w-4 h-4 text-green-500 dark:text-green-400" />
                          ) : isOverrideEvent ? (
                            <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          ) : (
                            <CircleDot className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </div>

                        {/* Event content */}
                        <div className="flex-1 min-w-0 pt-1">
                          {/* Main row with left content and right timestamp/progress */}
                          <div className="flex items-start justify-between gap-4">
                            {/* Left side: Action, role, worker info */}
                            <div className="flex-1 min-w-0">
                              {/* Action + Role badge */}
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-sm font-semibold ${
                                  isScanIn ? 'text-cyan-700 dark:text-cyan-300' :
                                  isScanOut ? 'text-orange-700 dark:text-orange-300' :
                                  isBreakStart ? 'text-amber-700 dark:text-amber-300' :
                                  isBreakEnd ? 'text-green-700 dark:text-green-300' :
                                  isStationAssigned ? 'text-blue-700 dark:text-blue-300' :
                                  isStationUnassigned ? 'text-red-700 dark:text-red-300' :
                                  isWeightModeChanged ? 'text-purple-700 dark:text-purple-300' :
                                  isReassignment ? 'text-amber-700 dark:text-amber-300' :
                                  isOperatorAssigned ? 'text-green-700 dark:text-green-300' :
                                  isPackerAssigned ? 'text-blue-700 dark:text-blue-300' :
                                  isUnassigned ? 'text-red-700 dark:text-red-300' :
                                  event.action === 'box_cancelled' ? 'text-red-700 dark:text-red-300' :
                                  event.action === 'material_issue' ? 'text-orange-700 dark:text-orange-300' :
                                  event.action === 'material_added' ? 'text-green-700 dark:text-green-300' :
                                  event.action === 'finish_product_added' ? 'text-emerald-700 dark:text-emerald-300' :
                                  event.action === 'transfer_verified' ? 'text-cyan-700 dark:text-cyan-300' :
                                  isOverrideEvent ? 'text-purple-700 dark:text-purple-300' :
                                  'text-gray-900 dark:text-white'
                                }`}>
                                  {isScanIn ? t('sessionDetails.scanIn') :
                                   isScanOut ? t('sessionDetails.scanOut') :
                                   isBreakStart ? (t('sessionDetails.breakStart') || 'Break Started') :
                                   isBreakEnd ? (t('sessionDetails.breakEnd') || 'Break Ended') :
                                   isStationAssigned ? (t('sessionDetails.historyStationAssigned') || 'Station Assigned') :
                                   isStationUnassigned ? (t('sessionDetails.historyStationUnassigned') || 'Station Unassigned') :
                                   isWeightModeChanged ? (t('sessionDetails.historyWeightModeChanged') || 'Weight Mode Changed') :
                                   isReassignment ? (t('sessionDetails.reassigned') || 'Reassigned') :
                                   isAssigned ? (t('sessionDetails.assigned') || 'Assigned') :
                                   isUnassigned ? (t('sessionDetails.unassigned') || 'Unassigned') :
                                   event.action === 'box_cancelled' ? (t('sessionDetails.boxCancelled') || 'Box Cancelled') :
                                   event.action === 'material_issue' ? (t('sessionDetails.materialIssue') || 'Raw Material Issue') :
                                   event.action === 'transfer_verified' ? (t('sessionDetails.materialReceived') || 'Material Received') :
                                   isOverrideEvent ? t(`sessionDetails.${event.action}`) || event.action.replace(/_/g, ' ') :
                                   event.action === 'created' ? t('sessionDetails.historyCreated') :
                                   event.action === 'submitted' ? t('sessionDetails.historySubmitted') :
                                   event.action === 'received' ? t('sessionDetails.historyReceived') :
                                   event.action === 'ready' ? t('sessionDetails.historyReady') :
                                   (event.action === 'started' || event.action === 'sub_session_started') ? t('sessionDetails.historyStarted') :
                                   event.action === 'completed' ? t('sessionDetails.historyCompleted') :
                                   event.action === 'cancelled' ? t('sessionDetails.historyCancelled') :
                                   (event.action === 'paused' || event.action === 'sub_session_paused') ? t('sessionDetails.historyPaused') :
                                   (event.action === 'resumed' || event.action === 'sub_session_resumed') ? t('sessionDetails.historyResumed') :
                                   event.action === 'excessive_reprints_resolved' ? t('sessionDetails.historyReprintsResolved') :
                                   event.action === 'subdivided' ? t('sessionDetails.historySubdivided') :
                                   event.action === 'material_added' ? t('sessionDetails.historyMaterialAdded') :
                                   event.action === 'finish_product_added' ? t('sessionDetails.historyFinishProductAdded') :
                                   event.action === 'transfer_reminder_created' ? t('sessionDetails.historyTransferReminderCreated') :
                                   event.action === 'transfer_reminder_resolved' ? t('sessionDetails.historyTransferReminderResolved') :
                                   event.action === 'edited' ? t('sessionDetails.historyEdited') :
                                   event.action === 'status_changed' ? t('sessionDetails.historyStatusChanged') :
                                   event.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </span>
                                {/* Role badge for scan, break, and assignment events */}
                                {(isScanEvent || isBreakEvent || isAssignmentEvent) && event.role && (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                    event.role === 'operator'
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                                  }`}>
                                    <User className="w-3 h-3" />
                                    {event.role === 'operator' ? t('sessionDetails.operator') : t('sessionDetails.packer')}
                                  </span>
                                )}
                              </div>

                              {/* Worker and station info for scan, break, and assignment events (from station_activity_log) */}
                              {(isScanEvent || isBreakEvent || isAssignmentEvent) && event.worker_name && (
                                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                  <span className="font-medium">
                                    {isReassignment ? `${event._reassigned_from} → ${event.worker_name}` : event.worker_name}
                                  </span>
                                  {event.station_name && (
                                    <>
                                      <span className="text-gray-400 dark:text-gray-500">•</span>
                                      <span className="text-gray-500 dark:text-gray-400">{event.station_name}</span>
                                    </>
                                  )}
                                  {event.sub_session_number && (
                                    <>
                                      <span className="text-gray-400 dark:text-gray-500">•</span>
                                      <span className="text-gray-500 dark:text-gray-400">#{event.sub_session_number}</span>
                                    </>
                                  )}
                                </div>
                              )}

                              {/* Assignment details from supervisor save (from session_history notes JSON) */}
                              {assignmentNotes && (
                                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                                  {assignmentNotes.station_name && isStationAssigned && (
                                    <span className="font-medium">
                                      {assignmentNotes.old_station_name ? `${assignmentNotes.old_station_name} → ${assignmentNotes.station_name}` : assignmentNotes.station_name}
                                    </span>
                                  )}
                                  {assignmentNotes.operator_name && isOperatorAssigned && (
                                    <span className="font-medium">{assignmentNotes.operator_name}</span>
                                  )}
                                  {assignmentNotes.packer_name && isPackerAssigned && (
                                    <span className="font-medium">{assignmentNotes.packer_name}</span>
                                  )}
                                  {isWeightModeChanged && assignmentNotes.old_mode && assignmentNotes.new_mode && (
                                    <span className="font-medium">
                                      {t(`sessionDetails.${assignmentNotes.old_mode}Weight`) || assignmentNotes.old_mode} → {t(`sessionDetails.${assignmentNotes.new_mode}Weight`) || assignmentNotes.new_mode}
                                    </span>
                                  )}
                                  {assignmentNotes.station_name && !isStationAssigned && (
                                    <>
                                      <span className="text-gray-400 dark:text-gray-500">•</span>
                                      <span className="text-gray-500 dark:text-gray-400">{assignmentNotes.station_name}</span>
                                    </>
                                  )}
                                  {assignmentNotes.sub_session_number && (
                                    <>
                                      <span className="text-gray-400 dark:text-gray-500">•</span>
                                      <span className="text-gray-500 dark:text-gray-400">#{assignmentNotes.sub_session_number}</span>
                                    </>
                                  )}
                                </div>
                              )}

                              {/* Status change for non-scan events */}
                              {!isScanEvent && event.old_status && event.new_status && event.old_status !== event.new_status && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {event.old_status} → {event.new_status}
                                </p>
                              )}

                              {/* Changed by for non-scan events */}
                              {!isScanEvent && event.changed_by && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {t('sessionDetails.by')}: {event.changed_by}
                                </p>
                              )}
                            </div>

                            {/* Right side: Timestamp and Progress */}
                            <div className="flex-shrink-0 text-right">
                              {/* Timestamp - more prominent */}
                              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                {eventTime}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {eventDate}
                              </div>

                              {/* Progress: show at session creation OR packer scan-out */}
                              {((event.action === 'created' && session.total_boxes) ||
                                (isScanOut && isPacker && event.boxes_at_event !== null)) && (
                                <div className="mt-2 px-3 py-1.5 rounded bg-gray-100 dark:bg-gray-700">
                                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    {event.action === 'created' ? 0 : event.boxes_at_event} / {session.total_boxes || '—'}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('sessionDetails.boxes').toLowerCase()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Packer stats for scan-out (final count) */}
                          {isScanOut && isPacker && event.boxes_packed_during_shift !== null && (
                            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
                              <Box className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                {t('sessionDetails.boxesPackedDuringShift')}: {event.boxes_packed_during_shift} {t('sessionDetails.boxes').toLowerCase()}, {Number(event.weight_packed_during_shift || 0).toFixed(2)} lbs
                              </span>
                            </div>
                          )}

                          {/* Live packer stats for active scan-in (updates in real-time) */}
                          {isActiveShift && (
                            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
                              <Box className="w-4 h-4 text-emerald-600 dark:text-emerald-400 animate-pulse" />
                              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300 inline-flex items-center">
                                {t('sessionDetails.packing') || 'Packing'}
                                <span className="inline-flex w-5 ml-0.5">
                                  <span className="animate-[pulse_1.5s_ease-in-out_infinite]">.</span>
                                  <span className="animate-[pulse_1.5s_ease-in-out_0.3s_infinite]">.</span>
                                  <span className="animate-[pulse_1.5s_ease-in-out_0.6s_infinite]">.</span>
                                </span>
                                {event.current_shift_boxes && event.current_shift_boxes > 0 && (
                                  <span className="ml-2 pl-2 border-l border-emerald-300 dark:border-emerald-600">
                                    {event.current_shift_boxes} {t('sessionDetails.boxes').toLowerCase()}, {Number(event.current_shift_weight || 0).toFixed(2)} lbs
                                  </span>
                                )}
                              </span>
                            </div>
                          )}

                          {/* Notes - formatted for override events, box_cancelled events, raw for others */}
                          {event.notes && (
                            isOverrideEvent && overrideNotes ? (
                              <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {overrideNotes.reason && (
                                  <p className="italic">{overrideNotes.reason}</p>
                                )}
                                {overrideNotes.previous_mode && overrideNotes.new_mode && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {overrideNotes.previous_mode} → {overrideNotes.new_mode}
                                  </p>
                                )}
                                {overrideNotes.operator_name && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {t('sessionDetails.operator')}: {overrideNotes.operator_name}
                                  </p>
                                )}
                              </div>
                            ) : event.action === 'cancelled' ? (
                              /* WCO-2706: notes hold a cancel-reason code (or legacy free text) */
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 italic">
                                {formatCancelReason(event.notes, t)}
                              </p>
                            ) : event.action === 'box_cancelled' && boxCancelledNotes ? (
                              <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {t('sessionDetails.box') || 'Box'} #{boxCancelledNotes.original_box_number} • {boxCancelledNotes.weight?.toFixed(2)} lbs
                                </p>
                                {boxCancelledNotes.reason && (
                                  <p className="italic mt-0.5">{boxCancelledNotes.reason}</p>
                                )}
                              </div>
                            ) : event.action === 'material_issue' && materialIssueNotes ? (
                              <div className="text-sm mt-1 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/50 rounded-md p-2">
                                {materialIssueNotes.reason && (
                                  <p className="italic text-gray-700 dark:text-gray-200 mb-1">"{materialIssueNotes.reason}"</p>
                                )}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                                  {materialIssueNotes.damaged_weight != null && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueDamagedWeight') || 'Damaged Weight'}:</span>
                                      <span className="font-medium text-orange-700 dark:text-orange-300">{Number(materialIssueNotes.damaged_weight || 0).toFixed(2)} lbs</span>
                                    </>
                                  )}
                                  {materialIssueNotes.item_number && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueItem') || 'Material'}:</span>
                                      <span className="font-medium">{materialIssueNotes.item_number}</span>
                                    </>
                                  )}
                                  {materialIssueNotes.transfer_number && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueLot') || 'Lot'}:</span>
                                      <span className="font-medium">{materialIssueNotes.transfer_number}</span>
                                    </>
                                  )}
                                  {materialIssueNotes.weight_before != null && materialIssueNotes.weight_after != null && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueInputWeight') || 'Input Weight'}:</span>
                                      <span className="font-medium">{Number(materialIssueNotes.weight_before).toFixed(2)} → {Number(materialIssueNotes.weight_after).toFixed(2)} lbs</span>
                                    </>
                                  )}
                                  {materialIssueNotes.target_before != null && materialIssueNotes.target_after != null && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueTargetOutput') || 'Target Output'}:</span>
                                      <span className="font-medium">{Number(materialIssueNotes.target_before).toFixed(2)} → {Number(materialIssueNotes.target_after).toFixed(2)} lbs</span>
                                    </>
                                  )}
                                  {materialIssueNotes.operator_name && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueReportedBy') || 'Reported By'}:</span>
                                      <span className="font-medium">{materialIssueNotes.operator_name}</span>
                                    </>
                                  )}
                                  {materialIssueNotes.weight_source && (
                                    <>
                                      <span>{t('sessionDetails.materialIssueMethod') || 'Method'}:</span>
                                      <span className="font-medium">{materialIssueNotes.weight_source === 'barcode' ? (t('sessionDetails.materialIssueScan') || 'Barcode Scan') : (t('sessionDetails.materialIssueManual') || 'Manual Entry')}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : event.action === 'weight_modified' && weightModifiedNotes ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {weightModifiedNotes.sub_session_number && (
                                  <span className="font-medium text-gray-700 dark:text-gray-200">{weightModifiedNotes.sub_session_number}: </span>
                                )}
                                {weightModifiedNotes.old_weight != null && weightModifiedNotes.new_weight != null && (
                                  Number(weightModifiedNotes.old_weight) !== Number(weightModifiedNotes.new_weight) ? (
                                    <>
                                      {Number(weightModifiedNotes.old_weight).toFixed(2)} → {Number(weightModifiedNotes.new_weight).toFixed(2)} lbs
                                      {weightModifiedNotes.difference != null && weightModifiedNotes.difference !== 0 && (
                                        <span className={`ml-1 font-medium ${weightModifiedNotes.difference > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                          ({weightModifiedNotes.difference > 0 ? '+' : ''}{Number(weightModifiedNotes.difference).toFixed(2)})
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <>{t('sessionDetails.weightSetTo') || 'Weight set to'} {Number(weightModifiedNotes.new_weight).toFixed(2)} lbs</>
                                  )
                                )}
                              </p>
                            ) : event.action === 'subdivided' && subdividedNotes ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {subdividedNotes.original_weight != null && subdividedNotes.original_remaining_weight != null ? (
                                  <>
                                    {subdividedNotes.original_sub_session_number && (
                                      <span className="font-medium text-gray-700 dark:text-gray-200">{subdividedNotes.original_sub_session_number}: </span>
                                    )}
                                    {Number(subdividedNotes.original_weight).toFixed(2)} → {Number(subdividedNotes.original_remaining_weight).toFixed(2)} lbs
                                    <span className="ml-1 font-medium text-red-600 dark:text-red-400">
                                      (-{Number(subdividedNotes.split_weight).toFixed(2)})
                                    </span>
                                    {subdividedNotes.new_sub_session_number && (
                                      <span className="ml-1">→ <span className="font-medium text-gray-700 dark:text-gray-200">{subdividedNotes.new_sub_session_number}</span></span>
                                    )}
                                  </>
                                ) : subdividedNotes.split_weight != null && (
                                  <>
                                    {Number(subdividedNotes.split_weight).toFixed(2)} lbs {t('sessionDetails.splitOff') || 'split off'}
                                    {subdividedNotes.original_remaining_weight != null && (
                                      <>, {Number(subdividedNotes.original_remaining_weight).toFixed(2)} lbs {t('sessionDetails.remainingAfterSplit') || 'remaining'}</>
                                    )}
                                  </>
                                )}
                                {subdividedNotes.reason && (
                                  <>
                                    <span className="mx-1 text-gray-400 dark:text-gray-500">•</span>
                                    {tReason('subdivideReasons', subdividedNotes.reason)}
                                  </>
                                )}
                                {subdividedNotes.reason_other && (
                                  <span className="italic"> — {subdividedNotes.reason_other}</span>
                                )}
                              </p>
                            ) : event.action === 'material_added' && materialAddedNotes ? (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {materialAddedNotes.item_number && (
                                  <span className="font-medium">{materialAddedNotes.item_number}</span>
                                )}
                                {materialAddedNotes.transfer_number && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span>{materialAddedNotes.transfer_number}</span></>
                                )}
                                {materialAddedNotes.weight != null && (
                                  <>
                                    <span className="mx-1 text-gray-400 dark:text-gray-500">•</span>
                                    {/* Older events have no before/after — fall back to the delta alone */}
                                    {materialAddedNotes.weight_before != null && materialAddedNotes.weight_after != null ? (
                                      <span>
                                        {Number(materialAddedNotes.weight_before).toFixed(2)} lbs
                                        {' + '}
                                        <span className="font-medium text-green-600 dark:text-green-400">{Number(materialAddedNotes.weight).toFixed(2)} lbs</span>
                                        {' = '}
                                        <span className="font-medium">{Number(materialAddedNotes.weight_after).toFixed(2)} lbs</span>
                                      </span>
                                    ) : (
                                      <span className="font-medium text-green-600 dark:text-green-400">+{Number(materialAddedNotes.weight).toFixed(2)} lbs</span>
                                    )}
                                  </>
                                )}
                                {materialAddedNotes.reason && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="italic">{tReason('addMaterialReasons', materialAddedNotes.reason)}</span></>
                                )}
                                {materialAddedNotes.reason_other && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="italic">{materialAddedNotes.reason_other}</span></>
                                )}
                              </p>
                            ) : event.action === 'transfer_verified' && transferVerifiedNotes ? (
                              <div className="text-sm mt-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-cyan-600 dark:text-cyan-400">WT {transferVerifiedNotes.transfer_number}</span>
                                </div>
                                {transferVerifiedNotes.items && transferVerifiedNotes.items.length > 0 && (() => {
                                  // Group items by action and sum weights/counts
                                  const grouped = transferVerifiedNotes!.items!.reduce((acc: Record<string, { count: number; weight: number }>, item: { item: string; action: string; weight: number; quantity?: number }) => {
                                    if (!acc[item.action]) acc[item.action] = { count: 0, weight: 0 };
                                    acc[item.action].count += Number(item.quantity) || 1;
                                    acc[item.action].weight += Number(item.weight) || 0;
                                    return acc;
                                  }, {} as Record<string, { count: number; weight: number }>);
                                  const actionOrder = ['approve', 'hold', 'reject'];
                                  const actionLabels: Record<string, string> = {
                                    approve: t('sessionDetails.received') || 'Received',
                                    hold: t('sessionDetails.onHold') || 'On Hold',
                                    reject: t('sessionDetails.rejected') || 'Rejected',
                                  };
                                  return (
                                    <div className="mt-1 space-y-0.5">
                                      {actionOrder.filter(a => grouped[a]).map(action => (
                                        <div key={action} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                                            action === 'approve' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                            action === 'reject' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                                          }`}>{actionLabels[action] || action}</span>
                                          <span className="font-medium">{grouped[action].weight.toFixed(2)} lbs</span>
                                          <span className="text-gray-500 dark:text-gray-400">
                                            ({grouped[action].count} {grouped[action].count === 1 ? t('common.case') || 'case' : t('common.cases') || 'cases'})
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : event.action === 'status_changed' && statusChangedNotes?.from && statusChangedNotes?.to ? (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {statusChangedNotes.from && statusChangedNotes.to && (
                                  <span className="font-medium">{tStatus(statusChangedNotes.from)} → {tStatus(statusChangedNotes.to)}</span>
                                )}
                                {event.sub_session_number && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="text-gray-500 dark:text-gray-400">#{event.sub_session_number}</span></>
                                )}
                              </p>
                            ) : event.action === 'finish_product_added' && (finishProductAddedNotes?.item_number || finishProductAddedNotes?.transfer_number || finishProductAddedNotes?.weight != null || finishProductAddedNotes?.reason) ? (
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                {finishProductAddedNotes.item_number && (
                                  <span className="font-medium">{finishProductAddedNotes.item_number}</span>
                                )}
                                {finishProductAddedNotes.transfer_number && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span>{finishProductAddedNotes.transfer_number}</span></>
                                )}
                                {finishProductAddedNotes.weight != null && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="font-medium text-emerald-600 dark:text-emerald-400">+{Number(finishProductAddedNotes.weight).toFixed(2)} lbs</span></>
                                )}
                                {finishProductAddedNotes.reason && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="italic">{tReason('addMaterialReasons', finishProductAddedNotes.reason)}</span></>
                                )}
                                {finishProductAddedNotes.sub_session_number && (
                                  <><span className="mx-1 text-gray-400 dark:text-gray-500">•</span><span className="text-gray-500 dark:text-gray-400">#{finishProductAddedNotes.sub_session_number}</span></>
                                )}
                              </p>
                            ) : assignmentNotes ? (
                              null  /* Already rendered above in the assignment details block */
                            ) : (
                              /* Generic fallback (WCO-2705): never dump a raw JSON blob. Notes that
                                 parse as an object render as a humanized key/value list; plain-text
                                 notes render as before. */
                              (() => {
                                const raw = (event.notes ?? '').trim();
                                if (!raw) return null;
                                let parsedNotes: any = null;
                                let parsed = false;
                                try {
                                  parsedNotes = JSON.parse(raw);
                                  parsed = true;
                                } catch {
                                  // Plain text notes (not JSON)
                                }
                                // Object notes → humanized key/value list.
                                if (parsed && parsedNotes && typeof parsedNotes === 'object' && !Array.isArray(parsedNotes)) {
                                  const entries = Object.entries(parsedNotes).filter(
                                    ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
                                  );
                                  if (entries.length === 0) return null;
                                  return (
                                    <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                                      {entries.map(([key, value]) => (
                                        <React.Fragment key={key}>
                                          <span>{humanize(key)}:</span>
                                          <span className="font-medium text-gray-700 dark:text-gray-200">{String(value)}</span>
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  );
                                }
                                // WCO-2819: array-shaped notes → a readable comma-joined line of scalar
                                // items (never a raw "[...]" blob). Nested objects/arrays are skipped.
                                if (parsed && Array.isArray(parsedNotes)) {
                                  const items = parsedNotes
                                    .filter((v) => v !== null && v !== undefined && v !== '' && typeof v !== 'object')
                                    .map((v) => String(v));
                                  if (items.length === 0) return null;
                                  return <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 italic">{items.join(', ')}</p>;
                                }
                                // WCO-2819: notes that parsed as JSON but aren't a renderable object/array
                                // (a bare number/boolean/string), OR that FAILED to parse but still look
                                // like JSON (start with { or [), must NOT be dumped raw. Only render as
                                // plain text when the note is genuinely non-JSON prose.
                                const looksLikeJson = raw.startsWith('{') || raw.startsWith('[');
                                if (parsed && typeof parsedNotes !== 'object') {
                                  return <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 italic">{String(parsedNotes)}</p>;
                                }
                                if (looksLikeJson) return null; // malformed JSON-looking blob — suppress rather than expose
                                return <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 italic">{raw}</p>;
                              })()
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                {t('sessionDetails.noHistory')}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SessionHistoryTab;
